/**
 * Wave 5 (E1) — default landing-org selection.
 *
 * After sign-in (and on org-list refresh) `AuthContext` needs to pick which
 * organisation becomes `currentOrganization` when no saved preference
 * exists in `localStorage`. The previous logic was `orgs[0]`, which in
 * practice is determined by Postgres select order and could land a
 * Coordinator with parent + inherited descendant memberships on a child
 * org — counter to the product expectation that Coordinator's natural
 * scope is the parent.
 *
 * Rule applied here ("highest ancestor the user has access to"):
 *
 *   1. Identify the topmost ancestors visible to the user — orgs whose
 *      `parent_org_id` is either NULL or not present in the user's own
 *      accessible list.
 *   2. Prefer a topmost org with `access_type === 'direct'` (real
 *      membership, not inherited via parent hierarchy). This matches the
 *      Coordinator product use case: their direct membership is at the
 *      parent, descendants are inherited.
 *   3. Fall back to any topmost ancestor (covers global admins who see
 *      every org with synthetic `access_type === 'global_admin'`).
 *   4. Final fallback: `orgs[0]` — preserves previous behaviour for
 *      degenerate inputs (flat lists with no parent_org_id resolvable).
 *
 * The helper is pure and deterministic — `AuthContext` keeps the
 * `savedOrgId` / `currentOrganization` precedence intact above this
 * fallback; only the "no saved preference" branch consults this picker.
 */

import type { OrganizationWithRole } from '@/types/organization';

export function pickDefaultOrganization<T extends OrganizationWithRole>(
  orgs: T[],
): T | null {
  if (!orgs || orgs.length === 0) return null;

  const idsInList = new Set<string>();
  for (const o of orgs) {
    if (o?.id) idsInList.add(o.id);
  }

  // Topmost ancestors: orgs whose parent is either NULL or not visible to
  // the user. Covers both true root parents and "highest visible ancestor"
  // for users whose access starts mid-tree.
  const topmost = orgs.filter((o) => {
    const pid = (o as { parent_org_id?: string | null }).parent_org_id ?? null;
    return !pid || !idsInList.has(pid);
  });

  // Prefer direct membership over inherited / synthetic global-admin so
  // Coordinator (direct = parent) lands on parent, not on an inherited
  // descendant. `direct` is the Phase-1 / Phase-6 access_type label.
  const directTopmost = topmost.filter((o) => o.access_type === 'direct');
  if (directTopmost.length > 0) return directTopmost[0];

  if (topmost.length > 0) return topmost[0];

  return orgs[0];
}
